import React, { useEffect, useMemo, useState } from 'react';
import { useConfirm, useToast } from '../context/UIContext';
import {
    addCompanyClosure,
    deleteCompanyClosure,
    getClosureConflicts,
    getCompanyClosures,
    updateCompanyClosure,
} from '../services/CompanyClosureService';
import { getAllUsers } from '../services/UserService';
import { extractApiError } from '../utils/apiError';
import { formatDate } from '../utils/dateConverter';
import '../styles/CompanyClosures.css';

const EMPTY = { start_date: '', end_date: '', description: '' };

/** Whole days in a span, both ends counted. */
function dayCount(start, end) {
    if (!start || !end) return 0;
    const a = new Date(`${start}T00:00:00`);
    const b = new Date(`${end}T00:00:00`);
    return Math.round((b - a) / 86400000) + 1;
}

/** Betriebsschließungstage: no contracted hours, no holiday consumed. A person listed as an
 *  exception works that span normally. */
export default function CompanyClosures() {
    const toast = useToast();
    const confirm = useConfirm();

    const [closures, setClosures] = useState([]);
    const [conflicts, setConflicts] = useState([]);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [editingExceptions, setEditingExceptions] = useState(null);
    const [draftExceptions, setDraftExceptions] = useState([]);

    const load = async () => {
        try {
            const [list, users, clashes] = await Promise.all([
                getCompanyClosures(),
                getAllUsers(),
                getClosureConflicts().catch(() => []),
            ]);
            setClosures(list);
            setPeople((users || []).filter(u => u.is_active !== false));
            setConflicts(clashes);
        } catch (e) {
            toast(extractApiError(e, 'Betriebsschließungen konnten nicht geladen werden.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nameById = useMemo(
        () => Object.fromEntries(people.map(p => [String(p.id), p.name || p.username])),
        [people],
    );

    const sorted = useMemo(
        () => [...closures].sort((a, b) => (a.start_date < b.start_date ? 1 : -1)),
        [closures],
    );

    const submit = async (e) => {
        e.preventDefault();
        // The API refuses an inverted span; saying so here saves the round trip.
        if (form.end_date && form.start_date && form.end_date < form.start_date) {
            toast('Das Enddatum darf nicht vor dem Startdatum liegen.');
            return;
        }
        setSaving(true);
        try {
            await addCompanyClosure({
                start_date: form.start_date,
                end_date: form.end_date || form.start_date,
                description: form.description || null,
            });
            setForm(EMPTY);
            toast('Betriebsschließung angelegt.');
            await load();
        } catch (err) {
            toast(extractApiError(err, 'Betriebsschließung konnte nicht angelegt werden.'));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (closure) => {
        const ok = await confirm(
            `Betriebsschließung ${formatDate(closure.start_date)} – ${formatDate(closure.end_date)} löschen? Die Tage zählen danach wieder als Arbeitstage.`,
            { confirmLabel: 'Löschen', danger: true },
        );
        if (!ok) return;
        try {
            await deleteCompanyClosure(closure.id);
            toast('Betriebsschließung gelöscht.');
            await load();
        } catch (e) {
            toast(extractApiError(e, 'Betriebsschließung konnte nicht gelöscht werden.'));
        }
    };

    const openExceptions = (closure) => {
        setEditingExceptions(closure.id);
        setDraftExceptions((closure.exception_user_ids || []).map(String));
    };

    const toggleException = (userId) => {
        const id = String(userId);
        setDraftExceptions(cur => (cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]));
    };

    const saveExceptions = async (closureId) => {
        setSaving(true);
        try {
            // Always sent, even empty: that is how the exceptions are cleared again.
            await updateCompanyClosure(closureId, { exception_user_ids: draftExceptions });
            setEditingExceptions(null);
            toast('Ausnahmen gespeichert.');
            await load();
        } catch (e) {
            toast(extractApiError(e, 'Ausnahmen konnten nicht gespeichert werden.'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <p className="cc-empty">Wird geladen…</p>;

    return (
        <div className="cc-wrap">
            <p className="cc-intro">
                An einem Betriebsschließungstag sind keine Arbeitsstunden fällig
            </p>

            <form className="cc-form" onSubmit={submit}>
                <label className="cc-field">
                    Von
                    <input
                        type="date"
                        className="input"
                        value={form.start_date}
                        required
                        onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                    />
                </label>
                <label className="cc-field">
                    Bis
                    <input
                        type="date"
                        className="input"
                        value={form.end_date}
                        min={form.start_date || undefined}
                        onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                    />
                </label>
                <label className="cc-field cc-field--grow">
                    Bezeichnung
                    <input
                        type="text"
                        className="input"
                        placeholder="z. B. Betriebsferien"
                        value={form.description}
                        onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    />
                </label>
                <button type="submit" className="button" disabled={saving || !form.start_date}>
                    Hinzufügen
                </button>
            </form>

            {conflicts.length > 0 && (
                <div className="cc-conflicts" role="status">
                    <strong>
                        Urlaub an Betriebsschließungstagen ({conflicts.length})
                    </strong>
                    <p className="cc-conflicts-hint">
                        Dieser Urlaub war eingetragen, bevor die Betriebsschließung definiert
                        wurde. Die Tage werden nicht automatisch zurückgegeben — bitte im
                        Urlaubskonto der Person korrigieren.
                    </p>
                    <ul>
                        {conflicts.map(c => (
                            <li key={`${c.holiday_id}-${c.closure_id}`}>
                                <strong>{c.user_name || c.user_id}</strong>
                                {': '}
                                {formatDate(c.overlap_start)}
                                {c.overlap_end !== c.overlap_start
                                    && ` – ${formatDate(c.overlap_end)}`}
                                {c.closure_description ? ` (${c.closure_description})` : ''}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {sorted.length === 0 ? (
                <p className="cc-empty">Noch keine Betriebsschließungstage eingetragen.</p>
            ) : (
                <table className="table cc-table table--cards">
                    <thead>
                        <tr>
                            <th>Zeitraum</th>
                            <th>Tage</th>
                            <th>Bezeichnung</th>
                            <th>Ausnahmen</th>
                            <th aria-label="Aktionen" />
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(closure => {
                            const ids = (closure.exception_user_ids || []).map(String);
                            const isEditing = editingExceptions === closure.id;
                            return (
                                <React.Fragment key={closure.id}>
                                    <tr>
                                        <td data-label="Zeitraum">
                                            {formatDate(closure.start_date)}
                                            {closure.end_date !== closure.start_date
                                                && ` – ${formatDate(closure.end_date)}`}
                                        </td>
                                        <td data-label="Tage">
                                            {dayCount(closure.start_date, closure.end_date)}
                                        </td>
                                        <td data-label="Bezeichnung">{closure.description || '—'}</td>
                                        <td data-label="Ausnahmen">
                                            {ids.length === 0
                                                ? 'gilt für alle'
                                                : ids.map(id => nameById[id] || id).join(', ')}
                                        </td>
                                        <td className="table-card-actions">
                                            <button
                                                type="button"
                                                className="button btn-sm"
                                                onClick={() => (isEditing
                                                    ? setEditingExceptions(null)
                                                    : openExceptions(closure))}
                                            >
                                                Ausnahmen
                                            </button>
                                            <button
                                                type="button"
                                                className="button button-danger btn-sm"
                                                onClick={() => remove(closure)}
                                            >
                                                Löschen
                                            </button>
                                        </td>
                                    </tr>
                                    {isEditing && (
                                        <tr className="cc-exception-row">
                                            <td colSpan={5}>
                                                <div className="cc-exceptions">
                                                    <p className="cc-exceptions-hint">
                                                        Wer hier angehakt ist, arbeitet in diesem
                                                        Zeitraum normal weiter.
                                                    </p>
                                                    <div className="cc-people">
                                                        {people.map(person => (
                                                            <label
                                                                key={person.id}
                                                                className="cc-person"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={draftExceptions.includes(String(person.id))}
                                                                    onChange={() => toggleException(person.id)}
                                                                />
                                                                <span>{person.name || person.username}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <div className="cc-exception-actions">
                                                        <button
                                                            type="button"
                                                            className="button btn-sm"
                                                            disabled={saving}
                                                            onClick={() => saveExceptions(closure.id)}
                                                        >
                                                            Speichern
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="button btn-sm"
                                                            onClick={() => setEditingExceptions(null)}
                                                        >
                                                            Abbrechen
                                                        </button>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
